import { Card, Divider, Pagination, Table, Typography, Tooltip, message, Space, Modal, Input, Row, Col, Statistic, Progress, Badge, Button, Select } from 'antd';
import { FullscreenOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import SearchFilter from '../../component/Filter';
import baseuri from '../../methods/baseuri';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAdminUnremittedBalances, retrieveAdminUnremittedBalancesStatus, resetAdminUnremittedBalances, runJobAdminUnremittedBalances } from '../../methods/redux/reducers/admin-unremitted-balances';
import { getCurrencies , getBlockchainNetworks} from '../../methods/redux/reducers/currency';
import remakeQueryString from '../../methods/utils/remake-query-string';
import NativeNumber from '../../component/Numeral';
import { commas, truncateAddress } from '../../methods/utils/general-utils';
import TableHeaderSection from '../../component/TableHeaderSection';
import getExplorerLink from '../../compliance/request-logs/getExplorerLink';
import { hideEmail } from '../../methods/utils/request-helpers';

const { Title, Text } = Typography;

export const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;

	& .myCard {
		border-radius: 10px;
		gap: 1em;
	}
`;

const HoverableAddress = styled(Text)`
	color: #060709ff !important;
	transition: color 0.3s;
	&:hover {
		color: #40a9ff !important;
		cursor: pointer;
	}
`;

const StyledStatusCard = styled(Card)<{ bgcolor?: string; bordercolor?: string }>`
	border-radius: 12px;
	border: 1px solid ${props => props.bordercolor || '#f0f0f0'};
	background: ${props => props.bgcolor || '#fff'};
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
	transition: all 0.3s ease;
	height: 100%;

	&:hover {
		box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	.ant-statistic-title {
		color: #595959;
		font-weight: 500;
		font-size: 14px;
	}

	.ant-statistic-content {
		font-weight: 700;
		font-size: 24px;
	}
`;

const UnremittedBalances = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const currentPage = Number(searchParams.get('page')) || 1;
	const pageSize = Number(searchParams.get('perPage')) || 20;
	const [isUnhashed, setIsUnhashed] = useState(false);
	const [isPasscodeModalVisible, setIsPasscodeModalVisible] = useState(false);
	const [passcode, setPasscode] = useState('');
	const [isResetModalVisible, setIsResetModalVisible] = useState(false);
	const [isRunJobModalVisible, setIsRunJobModalVisible] = useState(false);

	const [isTriggerModalVisible, setIsTriggerModalVisible] = useState(false);
	const [triggerPayload, setTriggerPayload] = useState<{ merchantId: string; walletAddresses: string[]; network: string; currency: string }>({
		merchantId: '',
		walletAddresses: [''],
		network: '',
		currency: '',
	});
	const [triggerResponse, setTriggerResponse] = useState<any>(null);
	const [isTriggering, setIsTriggering] = useState(false);

	const { unremittedBalances, jobStatus } = useAppSelector((store: any) => store.adminUnremittedBalances);
	const { currencies , blockchains} = useAppSelector((store: any) => store.currencies);

	const filterCurrencySwitchWalletId = (currency: string)=>{
		const currencyData = currencies.find((item: any) => item.symbol === currency);
		return currencyData?.switchWalletId;
	}

	const filterBlockchainSwitchWalletId = (blockchain: string)=>{
		const blockchainData = blockchains.find((item: any) => item.abbreviation === blockchain);
		return blockchainData?.switchWalletEnum;
	}

	const headers = [
		{ label: 'Balance', key: 'balance' },
		{ label: 'Currency Symbol', key: 'currencySymbol' },
		{ label: 'Balance In USD', key: 'balanceInUSD' },
		{ label: 'Chain Id', key: 'chainId' },
		{ label: 'Chain Name', key: 'chainName' },
	    { label: 'Currency Name', key: 'currencyName' },
		{ label: 'Decimal', key: 'decimals' },
		{ label: 'Wallet Address', key: 'walletAddress' },
		{ label: 'Contract Address', key: 'contractAddress' },
		{ label: 'Full Name', key: 'fullName' },
		{ label: 'Username', key: 'username' },
		{ label: 'User Email', key: 'userEmail' },
		{ label: 'Created At', key: 'createdAt' },
	];

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				page: '1',
				perPage: '20',
			});
		}

		dispatch(getCurrencies({}));
		dispatch(getBlockchainNetworks());
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const handlePasscodeSubmit = () => {
		if (passcode === '2026Xend') {
			setIsUnhashed(true);
			setIsPasscodeModalVisible(false);
			setPasscode('');
			message.success('Emails unhashed successfully');
		} else {
			message.error('Incorrect passcode');
		}
	};

	const handleReset = async () => {
		try {
			await dispatch(resetAdminUnremittedBalances()).unwrap();
			//  message.success('Unremitted balances reset successfully');
			setIsResetModalVisible(false);
			const queryData = Object.fromEntries(searchParams.entries());
			dispatch(retrieveAdminUnremittedBalances(queryData));
		} catch (error: any) {
			message.error(error.message || 'Failed to reset unremitted balances');
		}
	};

	const handleRunJob = async () => {
		try {
			await dispatch(runJobAdminUnremittedBalances()).unwrap();
			setIsRunJobModalVisible(false);
			const queryData = Object.fromEntries(searchParams.entries());
			dispatch(retrieveAdminUnremittedBalances(queryData));
		} catch (error: any) {
			message.error(error.message || 'Failed to start run job');
		}
	};

	const handleTriggerSubmit = async () => {
		try {
			setIsTriggering(true);
			setTriggerResponse(null);
			const cleanedAddresses = triggerPayload.walletAddresses.filter((a) => a.trim() !== '');
			const res = await baseuri.post('../hooks/webhooks/switchwallet/trigger-remittance', {
				merchantId: triggerPayload.merchantId,
				walletAddresses: cleanedAddresses,
				network: String(triggerPayload.network || ''),
				currency: String(triggerPayload.currency || ''),
			});
			setTriggerResponse(res.data);
			message.success('Remittance trigger completed!');
		} catch (error: any) {
			setTriggerResponse(error.response?.data || { error: error.message });
			message.error(error.response?.data?.message || 'Failed to trigger remittance');
		} finally {
			setIsTriggering(false);
		}
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveAdminUnremittedBalances(queryData));

		}
		dispatch(retrieveAdminUnremittedBalancesStatus());
	}, [searchParams]);

	useEffect(() => {
		let interval: any;
		if (jobStatus?.status === 'running') {
			interval = setInterval(() => {
				dispatch(retrieveAdminUnremittedBalancesStatus());
			}, 10000); // Poll every 10 seconds
		}
		return () => clearInterval(interval);
	}, [jobStatus?.status]);

	const currencySelect = currencies?.map((item: any) => ({
		name: item?.symbol,
		value: item?.symbol,
	}));

	const chainIdSelect = Array.from(new Set(unremittedBalances?.summary?.breakdown?.map((item: any) => item?.chainId) || []))
		.filter((id: any) => id !== undefined && id !== null)
		.map((chainId: any) => ({
			name: chainId,
			value: chainId,
		}));

	const columns = [
		{
			title: 'S/N',
			dataIndex: 'number',
			key: 'number',
			render: (_: any, __: any, index: number) => (
				<strong>
					{(currentPage - 1) * pageSize + index + 1}
				</strong>
			),
		},
					{
			title: 'User Email',
			dataIndex: 'userEmail',
			key: 'userEmail',
			render: (userEmail: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<a href={`/user-profile/${record?.userId?._id}`}><Text strong>{isUnhashed ? userEmail : hideEmail(userEmail)}</Text></a>
				</div>
			),
		},

		{
			title: 'Total Balance',
			dataIndex: 'balance',
			key: 'balance',
			render: (balance: any, record: any) => (
				<Text strong>
					<NativeNumber value={balance } /> {record?.currencySymbol}
				</Text>
			),
		},
		{
			title: 'Balance In USD',
			dataIndex: 'balanceInUSD',
			key: 'balanceInUSD',
			render: (balanceInUSD: any) => (
				<Text strong>
					$ <NativeNumber value={balanceInUSD} />
				</Text>
			),
		},

		{
			title: 'Chain Id',
			dataIndex: ['chainDetail', 'chainId'],
			key: 'chainId',
			render: (chainId: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record?.chainDetail?.blockchainNetwork?.iconUrl && <img src={record?.chainDetail?.blockchainNetwork?.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{chainId}</Text>
				</div>
			),
		},
		{
			title: 'Chain Name',
			dataIndex: ['chainDetail', 'chainName'],
			key: 'chainName',
			render: (chainName: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record?.chainDetail?.blockchainNetwork?.iconUrl && <img src={record?.chainDetail?.blockchainNetwork?.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{chainName}</Text>
				</div>
			),
		},
		{
			title: 'Currency Name',
			dataIndex: ['currencyDetail', 'name'],
			key: 'currencyName',
			render: (currencyName: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record?.currencyDetail?.currency?.iconUrl && <img src={record?.currencyDetail?.currency?.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{currencyName}</Text>
				</div>
			),
		},
			{
			title: 'Wallet Address',
			dataIndex: 'walletAddress',
			key: 'walletAddress',
			render: (walletAddress: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
			     <Link
						to={getExplorerLink(record?.chainDetail?.blockchainNetwork?.abbreviation, record?.walletAddress)}
						target="_blank"
						rel="noreferrer"
					>							
						<HoverableAddress strong>{walletAddress}</HoverableAddress>
					</Link>
				</div>
			),
		},
			{
			title: 'Contract Address',
			dataIndex: ['currencyDetail', 'contractAddress'],
			key: 'contractAddress',
			render: (contractAddress: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Text strong>{contractAddress || 'N/A'}</Text>
				</div>
			),
		},
		// 				{
		// 	title: 'Decimal',
		// 	dataIndex: 'decimals',
		// 	key: 'decimals',
		// 	render: (decimals: any, record: any) => (
		// 		<div style={{ display: 'flex', alignItems: 'center' }}>
		// 			<Text strong>{decimals}</Text>
		// 		</div>
		// 	),
		// },
	
		// 	{
		// 	title: 'Full Name',
		// 	dataIndex: 'userId',
		// 	key: 'userId',
		// 	render: (userId: any, record: any) => (
		// 		<div style={{ display: 'flex', alignItems: 'center' }}>
		// 			<Text strong>{userId?.fullName}</Text>
		// 		</div>
		// 	),
		// },

		// {
		// 	title: 'Username',
		// 	dataIndex: 'userId',
		// 	key: 'userId',
		// 	render: (userId: any, record: any) => (
		// 		<div style={{ display: 'flex', alignItems: 'center' }}>
		// 			<Text strong>{userId?.username}</Text>
		// 		</div>
		// 	),
		// },

	
				{
			title: 'Action',
			dataIndex: 'balance',
			key: 'action',
			render: (_: any, record: any) => (
				<Button
					type="primary"
					onClick={() => {
						setTriggerPayload({
							merchantId: record?.merchantId || record?.userId?.merchantId || '03ac933e-4b3a-4a13-d4d9-08d9a81de192',
							walletAddresses: [record?.walletAddress || ''],
							network: filterBlockchainSwitchWalletId(record?.chainDetail?.blockchainNetwork?.abbreviation) || '',
							currency: filterCurrencySwitchWalletId(record?.currencyDetail?.symbol) || '',
						});
						setTriggerResponse(null);
						setIsTriggerModalVisible(true);
					}}
				>
					Trigger Remittance
				</Button>
			),
		},

		{
			title: 'Created At',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (createdAt: string) => (
				<Text>{moment(createdAt)?.format('MMM DD, YY / hh:mm:ss a')}</Text>
			),
		},
	];

	return (
		<PageWrapper>
			<Card>
				<Title level={3}>Admin Unremitted Balances</Title>
				<Text>
					This page displays the list of unremitted balances for administrators.
				</Text>
				<Divider />
				<h1>Total Unremitted balance In USD: $ {Number(unremittedBalances?.summary?.totalUSDValue || 0)?.toLocaleString()}</h1>

				{jobStatus && (
					<div style={{ marginBottom: '24px' }}>
						<Title level={4}>Job Status: <Badge status={jobStatus?.status === 'running' ? 'processing' : 'success'} text={jobStatus?.status?.toUpperCase()} /></Title>
						<Row gutter={[16, 16]}>
							<Col span={6}>
								<StyledStatusCard bgcolor="#e6f7ff" bordercolor="#91d5ff">
									<Statistic title="Total Wallets" value={jobStatus?.totalWallets} valueStyle={{ color: '#0050b3' }} />
								</StyledStatusCard>
							</Col>
							<Col span={6}>
								<StyledStatusCard bgcolor="#f6ffed" bordercolor="#b7eb8f">
									<Statistic 
										title="Processed Wallets" 
										value={jobStatus?.processedWallets} 
										suffix={`/ ${jobStatus?.totalWallets}`} 
										valueStyle={{ color: '#389e0d' }}
									/>
									<Progress 
										percent={Math.round((jobStatus?.processedWallets / jobStatus?.totalWallets) * 100)} 
										size="small" 
										status={jobStatus?.status === 'running' ? 'active' : 'normal'}
										strokeColor="#52c41a"
									/>
								</StyledStatusCard>
							</Col>
							<Col span={6}>
								<StyledStatusCard bgcolor="#fffbe6" bordercolor="#ffe58f">
									<Statistic title="Balances Stored" value={jobStatus?.totalBalancesStored} valueStyle={{ color: '#d48806' }} />
								</StyledStatusCard>
							</Col>
							<Col span={6}>
								<StyledStatusCard bgcolor="#fff1f0" bordercolor="#ffa39e">
									<Statistic title="Errors" value={jobStatus?.errors} valueStyle={{ color: jobStatus?.errors > 0 ? '#cf1322' : '#595959' }} />
								</StyledStatusCard>
							</Col>
						</Row>
						{jobStatus.startedAt && (
							<div style={{ marginTop: '8px' }}>
								<Text type="secondary">Started At: {moment(jobStatus?.startedAt).format('MMM DD, YYYY / hh:mm:ss a')}</Text>
								{jobStatus?.durationMinutes && <Text type="secondary" style={{ marginLeft: '16px' }}>Duration: {jobStatus?.durationMinutes?.toFixed(2)} mins</Text>}
							</div>
						)}
					</div>
				)}

				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', marginTop: '16px' }}>
					<button
						className="ant-btn ant-btn-primary"
						onClick={() => {
							if (isUnhashed) {
								setIsUnhashed(false);
								message.success('Emails hashed');
							} else {
								setIsPasscodeModalVisible(true);
							}
						}}
					>
						{isUnhashed ? 'Hash Emails' : 'Unhash Emails'}
					</button>
					<button
					    disabled={unremittedBalances.paginator?.itemCount === 0}
						className="ant-btn ant-btn-danger"
						style={{ marginLeft: '8px', backgroundColor: '#ff4d4f', color: '#fff' }}
						onClick={() => setIsResetModalVisible(true)}
					>
						Reset Unremitted Balance
					</button>
					<button
					  disabled={unremittedBalances.paginator?.itemCount > 0}
						className="ant-btn ant-btn-primary"
						style={{ marginLeft: '8px' }}
						onClick={() => setIsRunJobModalVisible(true)}
					>
						Run Job
					</button>
				</div>

				<Divider />

				<SearchFilter
					title="Filter by Currency"
					searchFilters={[
						{
							label: 'Currency',
							name: 'currencySymbol',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'Chain Id',
							name: 'chainId',
							htmlElement: 'select',
							selectOptions: chainIdSelect,
						},
						{
							label: 'User Email',
							name: 'userEmail',
							htmlElement: 'input',
						},
						{
							label: 'Wallet Address',
							name: 'walletAddress',
							htmlElement: 'input',
						},
						{
							label: 'Is Native Token?',
							name: 'isNativeToken',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Yes', value: 'true' },
								{ name: 'No', value: 'false' },
							],
						},
						{
							label: 'Sort By',
							name: 'sortBy',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Balance', value: 'balance' },
								{ name: 'Balance In USD', value: 'balanceInUSD' },
							],
						},
						{
							label: 'Sort Order',
							name: 'sortOrder',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Ascending', value: 'asc' },
								{ name: 'Descending', value: 'desc' },
							],
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<Divider />

				<TableHeaderSection
					tableName="Total Unremitted Balances Count"
					currentPage={Number(searchParams.get('pageId')) || 1}
					// totalRecords={unremittedBalances.details?.paginator?.itemCount || 21}
					totalRecords={unremittedBalances.paginator?.itemCount || 0}
					downloadURL="/admin/unremitted-balances"
					csvHeaders={headers}
					isUnhashed={isUnhashed}
					csvDataTransformer={(items: any[]) =>
						items.map((item: any) => ({
							...item,
							balance: item.balance || 0,
							currencySymbol: item.currencyDetail?.symbol || 'N/A',
							chainId: item.chainDetail?.chainId || 'N/A',
							chainName: item.chainDetail?.chainName || 'N/A',
							currencyName: item.currencyDetail?.name || 'N/A',
							contractAddress: item.currencyDetail?.contractAddress || 'N/A',
							fullName: item.userId?.fullName || 'N/A',
							username: item.userId?.username || 'N/A',
							userEmail: isUnhashed ? item.userEmail : hideEmail(item.userEmail),
							createdAt: item.createdAt ? moment(item.createdAt).format('MMM DD, YY / hh:mm:ss a') : 'N/A',
						}))
					}
				/>

				<Table
					dataSource={unremittedBalances.itemsList || []}
					columns={columns}
					rowKey="_id"
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>

				<Divider />

				<Pagination
					onChange={(page, pageSize) => {
						updateQueryString({ page, perPage: pageSize });
					}}
					 total={unremittedBalances.paginator?.itemCount || 0}
					current={currentPage}
					pageSize={pageSize}
					showSizeChanger
					showQuickJumper
				/>

				<Modal
					title="Enter Passcode to Unhash Emails"
					visible={isPasscodeModalVisible}
					onOk={handlePasscodeSubmit}
					onCancel={() => setIsPasscodeModalVisible(false)}
				>
					<Input.Password
						placeholder="Enter passcode"
						value={passcode}
						onChange={(e) => setPasscode(e.target.value)}
						onPressEnter={handlePasscodeSubmit}
					/>
				</Modal>

				<Modal
					title="Confirm Reset"
					visible={isResetModalVisible}
					onOk={handleReset}
					onCancel={() => setIsResetModalVisible(false)}
					okText="Confirm Reset"
					cancelText="Cancel"
					okButtonProps={{ danger: true }}
				>
					<p>Are you sure you want to reset the unremitted balances? This action is permanent and will trigger a global reset.</p>
				</Modal>

				<Modal
					title="Confirm Run Job"
					visible={isRunJobModalVisible}
					onOk={handleRunJob}
					onCancel={() => setIsRunJobModalVisible(false)}
					okText="Confirm Run Job"
					cancelText="Cancel"
				>
					<p style={{ color: '#faad14', fontWeight: 'bold' }}>
						Please click the 'Reset Unremitted Balance' button before clicking the 'Run Job' button.
					</p>
					<p>Are you sure you want to run the unremitted balances job?</p>
				</Modal>

				<Modal
					title="Trigger Remittances"
					visible={isTriggerModalVisible}
					onCancel={() => { setIsTriggerModalVisible(false); setTriggerResponse(null); }}
					footer={[
						<Button key="cancel" onClick={() => { setIsTriggerModalVisible(false); setTriggerResponse(null); }}>
							Cancel
						</Button>,
						<Button key="submit" type="primary" loading={isTriggering} onClick={handleTriggerSubmit}>
							Trigger Remittance
						</Button>,
					]}
					width={600}
				>
					<Typography.Text>
						Trigger  remittance for unremitted balance
					</Typography.Text>

					<div style={{ marginTop: 16 }}>
						<label style={{ display: 'block', marginBottom: 4 }}>Merchant Id :</label>
						<Input 
							value={triggerPayload.merchantId} 
							onChange={(e) => setTriggerPayload({ ...triggerPayload, merchantId: e.target.value })}
							placeholder="Merchant Id"
							style={{ marginBottom: 16 }}
						/>

						<label style={{ display: 'block', marginBottom: 4 }}>Wallet Addresses</label>
						{triggerPayload.walletAddresses.map((address, index) => (
							<Input 
								key={index}
								value={address}
								onChange={(e) => {
									const newAddresses = [...triggerPayload.walletAddresses];
									newAddresses[index] = e.target.value;
									setTriggerPayload({ ...triggerPayload, walletAddresses: newAddresses });
								}}
								placeholder="Wallet Address"
								style={{ marginBottom: 8 }}
							/>
						))}

						<label style={{ display: 'block', marginBottom: 4 }}>Network:</label>
						<Select 
							value={String(triggerPayload.network)} 
							onChange={(val) => setTriggerPayload({ ...triggerPayload, network: String(val) || '' })}
							style={{ width: '100%', marginBottom: 16 }}
							options={chainIdSelect}
							allowClear
							disabled={true}
						/>

						<label style={{ display: 'block', marginBottom: 4 }}>Currency</label>
						<Select 
							value={triggerPayload.currency} 
							onChange={(val) => setTriggerPayload({ ...triggerPayload, currency: String(val) || '' })}
							style={{ width: '100%', marginBottom: 16 }}
							options={currencySelect}
							allowClear
							disabled={true}
						/>

						{triggerResponse && (
							<div style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8, marginTop: 16 }}>
								<pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontSize: 13 }}>
									{JSON.stringify(triggerResponse, null, 2)}
								</pre>
							</div>
						)}
					</div>
				</Modal>
			</Card>
		</PageWrapper>
	);
};

export default UnremittedBalances;
